// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { InteractiveObject } from "../interactive-object.js";
/**
 * This enumeration defines a list of planes for slicer.
 */
export var Slice_TypeOfPlane;
(function (Slice_TypeOfPlane) {
    Slice_TypeOfPlane[Slice_TypeOfPlane["XYPlane"] = 0] = "XYPlane";
    Slice_TypeOfPlane[Slice_TypeOfPlane["XZPlane"] = 1] = "XZPlane";
    Slice_TypeOfPlane[Slice_TypeOfPlane["YZPlane"] = 2] = "YZPlane";
})(Slice_TypeOfPlane || (Slice_TypeOfPlane = {}));
/**
 * Interactive slice object
 * Please use [[SliceManager]] to create slice object.
 */
export class InteractiveSlice extends InteractiveObject {
    /** Constructor.
     * @param theSliceId slice id.
     */
    constructor(theViewer, thePlane, theSliceId) {
        super(theViewer, theSliceId);
        this._myPlane = thePlane;
        this._myViewer.slicer.createPlane(thePlane);
    }
    /** Get flag signals that origin will depend on bbox size of selected objects e.g (0..1)->(minCorner..maxCorner) */
    get isRelative() { return this._myViewer.slicer.isRelative(this._myPlane); }
    /** Enable/disable relative. */
    set isRelative(theToEnable) {
        this._myViewer.slicer.setRelative(this._myPlane, theToEnable);
    }
    /** Get is display slice contour. */
    get isContourEnable() {
        return this._myViewer.slicer.isContourEnable(this._myPlane);
    }
    /** Enable/disable display slice contour. */
    set isContourEnable(theToEnable) {
        this._myViewer.slicer.setContourEnable(this._myPlane, theToEnable);
    }
    /** Get normal */
    get normal() { return this._myViewer.slicer.normal(this._myPlane); }
    /** Set normal */
    set normal(theNormal) {
        this._myViewer.slicer.setNormal(this._myPlane, theNormal);
    }
    /** Get Offset of slice. */
    get offset() { return this._myViewer.slicer.offset(this._myPlane); }
    /** Set Offset of slice. */
    set offset(theValue) { this._myViewer.slicer.setOffset(this._myPlane, theValue); }
    /** Get Origin of slice. */
    get origin() { return this._myViewer.slicer.origin(this._myPlane); }
    /** Set Origin of slice. */
    set origin(theValue) {
        this._myViewer.slicer.setOrigin(this._myPlane, theValue);
    }
    /** Get the minimum possible position of the slice in the coordinate of the plane axis. */
    get minAxisPosition() {
        return this._myViewer.slicer.getMinAxisPosition(this._myPlane);
    }
    /** Get the maximum possible position of the slice in the coordinate of the plane axis. */
    get maxAxisPosition() {
        return this._myViewer.slicer.getMaxAxisPosition(this._myPlane);
    }
    /** Force recompute */
    forceRecompute() { this._myViewer.slicer.forceRecompute(this._myPlane); }
}
/**
 * Slice manager object is used to create and manage slice objects.
 * Slice object represents the set of lines on intersection of the plane and objects.
 * Slice object is interactive object and can be used to change slice position.
 */
export class SliceManager {
    /** Constructor.
     * @param theObjIds id objects which should be sliced
     */
    constructor(theViewer, theObjIds) {
        this._mySlices = [];
        this._myViewer = theViewer;
        if (theViewer != null) {
            theViewer.slicer.objects = theObjIds;
            this._mySlices = [
                {
                    slice: new InteractiveSlice(this._myViewer, Slice_TypeOfPlane.XYPlane, this._myViewer.slicer.getSliceId(Slice_TypeOfPlane.XYPlane))
                },
                {
                    slice: new InteractiveSlice(this._myViewer, Slice_TypeOfPlane.XZPlane, this._myViewer.slicer.getSliceId(Slice_TypeOfPlane.XZPlane))
                },
                {
                    slice: new InteractiveSlice(this._myViewer, Slice_TypeOfPlane.YZPlane, this._myViewer.slicer.getSliceId(Slice_TypeOfPlane.YZPlane))
                }
            ];
        }
    }
    /** Set obgects list. */
    set objects(theObjId) { this._myViewer.slicer.objects = theObjId; }
    /** Get obgects list. */
    get objects() { return this._myViewer.slicer.objects; }
    /** Get objects for bounding box calculation. */
    get bndBoxObjects() { return this._myViewer.slicer.bndBoxObjects; }
    /** Set objects for bounding box calculation. */
    set bndBoxObjects(theObjId) { this._myViewer.slicer.bndBoxObjects = theObjId; }
    /** Returns InteractiveSlice object. */
    getSlice(thePlane) {
        return this._mySlices[thePlane].slice;
    }
    /** Get line width. */
    get lineWidth() { return this._myViewer.slicer.lineWidth; }
    /** Set line width. */
    set lineWidth(theWidth) { this._myViewer.slicer.lineWidth = theWidth; }
    /** Color in `#rrggbbaa` format. */
    set color(theColor) { this._myViewer.slicer.setColor(theColor); }
    /** Enable/disable showing axes*/
    set axesEnabled(theAxesEnabled) {
        this._myViewer.slicer.setAxesEnabled(theAxesEnabled);
    }
    /** Set color for object */
    setColorForObject(theObjectId, theColor) {
        this._myViewer.slicer.setColorForObject(theObjectId, theColor);
    }
    ;
}
